import MuscleSvg from '../../../../images/change-goal-pictures/muscle-gain-2020.png'
import WeightSvg from '../../../../images/change-goal-pictures/weight-loss-2020.png';
import ReverseDietingSvg from '../../../../images/change-goal-pictures/reverse-2020.png'

export const toLocal = (currentUnit, wKg) => {
    // If local unit is kg then kg are returned, if not, then lbs.
    return currentUnit === 1 ? wKg : Math.min(wKg * 2.204622, 699.9);
};

export function unitShort(useMetricUnits) {
  return useMetricUnits ? 'kg' : 'lbs';
}

export const signGradients = [
  ['#00ee46', '#0e9e38'],
  ['#bad32b', '#7f901d'],
  ['#e39428', '#ff7d37'],
  ['#ff4415', '#e03c13'],
  ['#e32828', '#ff3773'],
];
export const signLevels = [
  'Low',
  'Low-Medium',
  'Medium',
  'Medium-High',
  'High',
];
export const sliderGradient = ['#1f9e0e', '#e8902a', '#ee0000'];

function calcIndex(index, range, dataCount) {
  return Math.min(Math.trunc(index / range / (1 / dataCount)), dataCount - 1);
}

export class GoalData {
  static multiplierRange = () => []; // required in subclasses
  static titles = []; // required in subclasses
  // Making sure references are the same everywhere.
  static get(key) {
    const data = [MuscleGain, FatLoss, ReverseDieting, Maintenance];
    const cls = data.filter(c => c.key === key)[0];
    if (cls == null) {
      throw Error(
        `No level data matches the given key ${key}. Available keys [${data
          .map(c => c.key)
          .join(', ')}]`,
      );
    }
    return cls;
  }
  static mapRatioToRange(ratio, multiplierRange) {
    const [start, end] = multiplierRange;
    return start + (end - start) * ratio;
  }
  static getRatioFrom(value, multiplierRange) {
    const [start, end] = multiplierRange;
    const ratio = (value - start) / (end - start);
    return Math.max(Math.min(ratio, 1), 0);
  }
  static sliderPosition(props) {
    // goalMinWeeklyCalIncrease, gender for reverse
    // goalWeightDelta, weight for muscle/fat loss
    // TODO this points to a need for a constructor.
    return Math.round(this.propsToRatio(props) * 100);
  }
  static signIndex(index, range) {
    return calcIndex(index, range, signLevels.length);
  }
  static coloredLabelIndex(index, range) {
    return calcIndex(index, range, this.titles.length);
  }
  static subtitleStyle(color) {
    return {x: '35%', y: '70%', fontSize: 13, fill: color};
  }
  static centralText(ratio, {weight, useMetricUnits}) {
    // should override
    let goalLevelWeight =
      weight * this.mapRatioToRange(ratio, this.multiplierRange());
    const unit = useMetricUnits === 1 ? ' kg' : ' lbs';
    if (useMetricUnits !== 1) {
      goalLevelWeight *= 2.204622;
    }
    return goalLevelWeight.toFixed(2) + unit;
  }
  static resultValue(ratio, {weight}) {
    // should override
    return weight * this.mapRatioToRange(ratio, this.multiplierRange());
  }
  static propsToRatio({goalWeightDelta, weight}) {
    // should override
    return this.getRatioFrom(goalWeightDelta / weight, this.multiplierRange());
  }
  static goalSubtitle({goalWeightDelta, weight, useMetricUnits}) {
    // should override
    const range = 100;
    const index = this.sliderPosition({goalWeightDelta, weight});
    const title = this.titles[this.coloredLabelIndex(index, range)];
    const color = signGradients[this.signIndex(index, range)][0];
    const subtitle = `${title}: ${toLocal(
      useMetricUnits,
      goalWeightDelta,
    ).toFixed(2)} ${useMetricUnits === 1 ? 'kg' : 'lbs'} per week`;
    return {subtitle, subtitleStyle: this.subtitleStyle(color)};
  }
}

class MuscleGain extends GoalData {
  static key = 'MUSCLE GAIN';
  static title = 'How quickly do you want to build muscle?';
  static icon = MuscleSvg;
  static descriptions = [
    'Ensures a slow and steady increase in lean body mass while minimizing the risk for gaining body fat. Ideal for the user who wants to add strength and gradually build muscle while remaining lean.',
    'Increases the rate at which the body will add lean mass, but also slightly increases the risk of additional weight gain outside of muscle tissue. Ideal for the average user seeking to build muscle.',
    'Enables the body to build lean mass and strength quickly, but is coupled with an accepted risk of fat gain. Ideal for the athlete with a high energy output preparing for an upcoming competitive season in a relatively short window.',
  ];
  static titles = ['Lean', 'Moderate', 'Aggressive'];
  static weightText = 'Expected Weekly Gain: ';
  static signText = 'Fat gain risk: ';
  static info = {
    title: 'Keep in mind that…',
    text:
      'There’s a limit to the amount of muscle you can build each week, and the extra calories you don’t burn off or use to make muscle will be stored as fat. If you want to stay more lean, choose a slower rate of muscle gain since this will require eating fewer calories than a faster rate.',
  };
  static multiplierRange = () => [0.002, 0.005];
}

class FatLoss extends GoalData {
  static key = 'FAT LOSS';
  static title = 'How quickly do you want to lose weight?';
  static icon = WeightSvg;
  static descriptions = [
    'Ideal for the typical dieter looking to burn fat safely, effectively, and with the greatest chance for long-term success.',
    'A sweet-spot for members who can handle lower macros to see slightly faster progress each week.',
    'An option for rare cases when short-term, aggressive weight loss is necessary. Acceptable only for members who can handle very low macros and possibly sacrificing some muscle for significant, rapid fat loss.',
  ];

  static titles = ['Sustainable', 'Moderate', 'Aggressive'];
  static weightText = 'Expected Weekly Loss: ';
  static signText = 'Hunger level: ';
  static info = {
    title: 'Keep in mind that…',
    text:
      'Choosing to lose weight more quickly means you’ll be eating less food and possibly fighting more hunger. The best diet is the one you can stick to, so if you tend to overeat when hungry or are worried that low calories might hurt your energy or performance, you should choose a slower rate of weight loss.',
  };
  static multiplierRange = () => [0.004, 0.012];
}

class ReverseDieting extends GoalData {
  static key = 'REVERSE DIETING';
  static title = 'How quickly do you want to add calories back to your diet?';
  static icon = ReverseDietingSvg;
  static titles = ['Conservative', 'Moderate', 'Aggressive'];
  static weightText = 'Calories added weekly approximately: ';
  static signText = 'Weight gain risk: ';
  static info = {
    title: 'Why add calories each week?',
    text:
      'By eating a little more each week, rather than all at once, you’ll gradually rebuild your metabolism and bring your food intake back to pre-diet levels so you can keep lost weight off.',
  };
  static multiplierRange = gender =>
    gender === 'FEMALE' ? [30, 60] : [35, 65];

  static centralText(ratio, {gender}) {
    const start = this.resultValue(ratio, {gender});
    const end = start + 15;
    return `${start.toFixed(0)}-${end.toFixed(0)}`;
  }
  static resultValue(ratio, {gender}) {
    // overriding
    return this.mapRatioToRange(ratio, this.multiplierRange(gender));
  }
  static propsToRatio({goalMinWeeklyCalIncrease, gender}) {
    // overriding
    return this.getRatioFrom(
      goalMinWeeklyCalIncrease,
      this.multiplierRange(gender),
    );
  }
  static goalSubtitle(props) {
    // goalMinWeeklyCalIncrease and gender as props
    const {goalMinWeeklyCalIncrease: gmwci} = props;
    // should override
    const range = 100;
    const index = this.sliderPosition(props);
    const title = this.titles[this.coloredLabelIndex(index, range)];
    const color = signGradients[this.signIndex(index, range)][0];
    const subtitle = `${title}: ${gmwci}-${gmwci + 15} cal/wk`;
    const subtitleStyle = {x: '35%', y: '70%', fontSize: 13, fill: color};
    return {subtitle, subtitleStyle};
  }
}

class Maintenance extends GoalData {
  static key = 'MAINTENANCE';
  static goalSubtitle({goalWeightDelta, targetWeight, useMetricUnits: umu}) {
    const weight = toLocal(umu, targetWeight);
    const value = toLocal(umu, goalWeightDelta);
    const maintenanceRange = Math.round((value / weight) * 1000) / 1000;
    const range = maintenanceRange * weight;
    const s = Math.round((weight - range) * 10) / 10;
    const e = Math.round((weight + range) * 10) / 10;

    const subtitle = `Weight range: ${s}-${e} ${unitShort(umu)}`;
    const subtitleStyle = {x: '35%', y: '70%', fontSize: 13, fill: '#fff'};
    return {subtitle, subtitleStyle};
  }
}
export const values = ['EASY', 'NORMAL', 'HARD'];
